import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import type { SpecimenListSearch } from '../types/product.types';
import { toCatalogueFilterSearch, toCatalogueFilterValues } from '../utils/productFilters';

const ROUTES = {
  specimens: getRouteApi('/_auth/product/specimens'),
  specimenListView: getRouteApi('/_auth/product/specimen-list-view'),
} as const;

export type CatalogueRouteId = keyof typeof ROUTES;

const normalizeFilterValues = (values: Record<string, string>) =>
  toCatalogueFilterValues(toCatalogueFilterSearch(values));

export function useCatalogueFilters(routeId: CatalogueRouteId) {
  const route = ROUTES[routeId];
  const search: SpecimenListSearch = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toCatalogueFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toCatalogueFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const nameSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...nameSearch, search };
}
